import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment, RadioGroup, Radio, Paper, Pagination, Dialog, DialogTitle, DialogContent, DialogActions, Divider, FormControl, InputLabel, Chip,
  Tooltip, Autocomplete, Stack, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar,
  Menu, ListItemIcon, ListItemText
} from "@mui/material";
import { Helmet } from "react-helmet";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import BulkSpecialPrayerDialog from "../components/BulkSpecialPrayerDialog";
import { downloadSpecialPrayerTemplate } from "../utils/ExcelPrayerService";
import { useUser } from "../context/UserContext";
import DOMPurify from 'dompurify';
import PageLoader from "../components/PageLoader";
import SectionLoader from "../components/SectionLoader";
import { useLocation, useNavigate, useSearchParams, Outlet } from 'react-router-dom';
import Toast from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";
import ViewUserActivityModal from "../components/ViewUserActivityModal";
import TimelineIcon from "@mui/icons-material/Timeline";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PublishIcon from "@mui/icons-material/Publish";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupIcon from "@mui/icons-material/Group";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CategoryIcon from "@mui/icons-material/Category";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArchiveManager from "../utils/archiveManager";
import UnpublishedManager from "../utils/unpublishedManager";
import HelpTooltip from "../components/HelpTooltip";
import SettingsIcon from '@mui/icons-material/Settings';
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import DraftsIcon from "@mui/icons-material/Drafts";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import MobilePrayerPreviewModal from "../components/MobilePrayerPreviewModal";

// Safe date and time slot formatters for details modal
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(dateStr);
  }
};

const formatTimeSlot = (slot) => {
  if (!slot) return 'Ongoing';
  if (Array.isArray(slot)) return slot.join(', ');
  return String(slot);
};

function StatCard({ label, count, IconComponent, active = false, onClick }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const bgDefault = isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)';
  const bgActive = isLight
    ? 'linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)'
    : 'linear-gradient(135deg, rgba(76, 161, 175, 0.28) 0%, rgba(44, 62, 80, 0.5) 100%)';
  const borderDefault = isLight ? '1.5px solid #94A3B8' : '1px solid rgba(255,255,255,0.1)';
  const borderActive = isLight ? '2.5px solid #0D9488' : '2px solid #4CA1AF';
  const countColor = isLight ? (active ? '#0D9488' : '#0F172A') : '#FFFFFF';
  const labelColor = isLight ? (active ? '#0F766E' : '#1E293B') : (active ? '#FFFFFF' : 'rgba(255,255,255,0.85)');
  const iconBg = active
    ? (isLight ? '#0D9488' : '#4CA1AF')
    : (isLight ? '#E6FFFA' : 'rgba(76, 161, 175, 0.2)');
  const iconColor = active ? '#FFFFFF' : (isLight ? '#0D9488' : '#4CA1AF');

  return (
    <Card
      onClick={onClick}
      variant="outlined"
      sx={{
        height: "100%",
        minWidth: 0,
        width: "100%",
        borderRadius: 2.5,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        background: active ? bgActive : bgDefault,
        backdropFilter: 'blur(20px)',
        border: active ? borderActive : borderDefault,
        boxShadow: active
          ? (isLight ? '0 6px 20px rgba(13, 148, 136, 0.25)' : '0 8px 24px rgba(76, 161, 175, 0.35)')
          : (isLight ? '0 2px 6px rgba(15, 23, 42, 0.06)' : '0 2px 8px rgba(0, 0, 0, 0.1)'),
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          transform: 'translateY(-3px)',
          borderColor: active ? borderActive : (isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.5)'),
          background: active
            ? bgActive
            : (isLight ? '#FFFFFF' : 'rgba(76, 161, 175, 0.12)'),
          boxShadow: isLight ? '0 8px 24px rgba(15, 23, 42, 0.12)' : '0 8px 20px rgba(0, 0, 0, 0.3)',
        } : {},
      }}
    >
      <CardContent sx={{ minHeight: 84, p: '16px 18px !important' }}>
        <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
          <Box
            sx={{
              height: 50,
              width: 50,
              background: iconBg,
              borderRadius: 2,
              border: isLight && !active ? '1.5px solid #99F6E4' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              flexShrink: 0,
              transition: 'all 0.25s ease',
              boxShadow: active ? (isLight ? '0 4px 12px rgba(13, 148, 136, 0.35)' : '0 4px 12px rgba(76, 161, 175, 0.4)') : 'none',
            }}
          >
            <IconComponent sx={{ fontSize: 30, color: iconColor }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="h4" fontWeight={800} noWrap sx={{ color: countColor, fontSize: '1.65rem', lineHeight: 1.2 }}>
                {count}
              </Typography>
              {active && (
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.3,
                    borderRadius: 1,
                    bgcolor: isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.3)',
                    border: isLight ? 'none' : '1px solid rgba(76, 161, 175, 0.6)',
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Active
                </Box>
              )}
            </Box>
            <Typography variant="body2" noWrap sx={{ color: labelColor, fontWeight: 700, fontSize: '0.92rem', mt: 0.3 }}>
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// Function to strip HTML and decode HTML entities
const stripHtml = (html) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = DOMPurify.sanitize(html); // prevents XSS
  return tempDiv.textContent || tempDiv.innerText || '';
};


const SpecialPrayers = ({
  hideHeader = false,
  onNavigateTab,
  initialFilterTab = null,
  isPendingMode = false,
  isUnpublishedMode = false,
  isArchivedMode = false
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchParams] = useSearchParams();
  const [filterActiveTab, setFilterActiveTab] = useState(() => {
    if (initialFilterTab) return initialFilterTab;
    const tabParam = searchParams.get("tab");
    if (tabParam === "pending") return "pending";
    if (tabParam === "unpublished") return "unpublished";
    if (["active", "thisMonth", "this_month", "archived"].includes(tabParam)) return tabParam;
    return "active";
  });
  const isUnpublishedView = Boolean(
    isUnpublishedMode ||
    initialFilterTab === "unpublished" ||
    filterActiveTab === "unpublished" ||
    searchParams.get("tab") === "unpublished"
  );
  const isPendingView = !isUnpublishedView && Boolean(
    isPendingMode ||
    initialFilterTab === "pending" ||
    filterActiveTab === "pending" ||
    searchParams.get("tab") === "pending"
  );
  const isArchivedView = Boolean(
    isArchivedMode ||
    initialFilterTab === "archived" ||
    filterActiveTab === "archived" ||
    searchParams.get("tab") === "archived"
  );
  const [selectedCategory, setSelectedCategory] = useState(() => searchParams.get("category") || "all");
  const [closetCounts, setClosetCounts] = useState({ all: 0, active: 0, pending: 0, unpublished: 0, thisMonth: 0 });

  useEffect(() => {
    if (initialFilterTab) {
      setFilterActiveTab(initialFilterTab);
    }
  }, [initialFilterTab]);

  useEffect(() => {
    const catQuery = searchParams.get("category");
    if (catQuery) {
      setSelectedCategory(catQuery);
    }
    const tabQuery = searchParams.get("tab");
    if (tabQuery && ["all", "active", "pending", "unpublished", "thisMonth", "this_month", "archived"].includes(tabQuery)) {
      setFilterActiveTab(tabQuery);
    }
  }, [searchParams]);


  const [organization, setOrganization] = useState(null); // or orgOptions[0] for default
  const [devotionList, setDevotionList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const { user, isSwitched, effectiveOrgId, orgViewMode } = useUser();
  const isSwitchedOrgMode = Boolean(
    isSwitched ||
    orgViewMode ||
    effectiveOrgId ||
    user?.is_switched_from_super_admin ||
    sessionStorage.getItem('gpn_sa_token') ||
    localStorage.getItem('gpn_sa_token') ||
    sessionStorage.getItem('gpn_org_view_mode') ||
    localStorage.getItem('gpn_org_view_mode')
  );
  const showOrganizationColumn = Boolean(user?.role === 1 && !isSwitchedOrgMode && !user?.org_id);
  const currentOrgKey = isSwitched ? (effectiveOrgId || user?.org_id) : (user?.role === 1 ? 'global' : user?.org_id);
  const [organizations, setOrganizations] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [templatesList, setTemplatesList] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('special-prayer'));
  const [unpublishedList, setUnpublishedList] = useState(() => UnpublishedManager.getUnpublishedIds('special-prayer'));
  const [rowMenu, setRowMenu] = useState({ anchorEl: null, devotion: null });
  const [draftsModalOpen, setDraftsModalOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('special-prayer', currentOrgKey));
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [selectedPreviewPrayer, setSelectedPreviewPrayer] = useState(null);
  const handleOpenMobilePreview = async (prayer) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}special-prayer-details`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: prayer.id })
      });
      const resData = await response.json();
      const prayerDetails = (resData?.success && resData?.data) ? resData.data : prayer;

      const rawPoints = prayerDetails.session_prayer_points || [];
      const dayWise = {};
      rawPoints.forEach((pt) => {
        const d = pt.day || 1;
        if (!dayWise[d]) {
          dayWise[d] = {
            title: prayerDetails.title,
            prayerPoints: []
          };
        }
        dayWise[d].prayerPoints.push({
          title: pt.category || pt.prayer_point || 'Prayer Point',
          description: pt.prayer_point || pt.category || '',
          image: pt.day_img || pt.image || null,
        });
      });

      setSelectedPreviewPrayer({
        ...prayerDetails,
        points: rawPoints.map(pt => ({
          title: pt.category || pt.prayer_point || 'Prayer Point',
          description: pt.prayer_point || pt.category || '',
          image: pt.day_img || pt.image || null,
        })),
        days: dayWise
      });
      setMobilePreviewOpen(true);
    } catch (e) {
      console.error("Error fetching preview prayer details:", e);
      setSelectedPreviewPrayer(prayer);
      setMobilePreviewOpen(true);
    } finally {
      setLoading(false);
    }
  };
  const [userActivityModalOpen, setUserActivityModalOpen] = useState(false);
  const [selectedClosetForActivity, setSelectedClosetForActivity] = useState(null);

  // Sync drafts whenever the active organization changes
  useEffect(() => {
    setDraftsList(DraftManager.getDrafts('special-prayer', currentOrgKey));
  }, [currentOrgKey]);

  // Handle toast notification and draft refresh from AddSpecialPrayer or storage
  useEffect(() => {
    let pendingToast = null;
    if (location.state?.toast) {
      pendingToast = location.state.toast;
      window.history.replaceState({}, document.title);
    } else {
      try {
        const stored = sessionStorage.getItem("gpn_pending_draft_toast");
        if (stored) {
          sessionStorage.removeItem("gpn_pending_draft_toast");
          pendingToast = JSON.parse(stored);
        }
      } catch (err) {
        console.error("Error reading pending draft toast:", err);
      }
    }

    if (pendingToast) {
      setToast({
        open: true,
        message: pendingToast.message || "Draft saved to Prayer Closet successfully",
        severity: pendingToast.severity || "success",
      });
      setDraftsList(DraftManager.getDrafts("special-prayer", currentOrgKey));
    }
  }, [location.state, currentOrgKey]);

  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];

  const [selectedClosetEntry, setSelectedClosetEntry] = useState(null);
  const [closetDetailModalOpen, setClosetDetailModalOpen] = useState(false);

  const handleViewPrayerCloset = (devotion) => {
    setSelectedClosetEntry(devotion);
    setClosetDetailModalOpen(true);
  };

  const handleCloseClosetDetailModal = () => {
    setClosetDetailModalOpen(false);
    setSelectedClosetEntry(null);
  };

  const handleArchive = (devotion) => {
    setConfirmDialog({
      open: true,
      title: "Archive Special Prayer",
      message: "Are you sure you want to archive this prayer? It will no longer be visible to mobile app users until restored.",
      onConfirm: () => {
        ArchiveManager.archiveItem('special-prayer', devotion);
        setArchivedList(ArchiveManager.getArchivedItems('special-prayer'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        if (closetDetailModalOpen) handleCloseClosetDetailModal();
        setToast({ open: true, message: "Prayer archived and hidden from mobile app.", severity: "info" });
      }
    });
  };

  const handleRestore = (devotionId) => {
    ArchiveManager.restoreItem('special-prayer', devotionId);
    setArchivedList(ArchiveManager.getArchivedItems('special-prayer'));
    setToast({ open: true, message: "Prayer restored to active status!", severity: "success" });
  };

  const handlePermanentDelete = (devotionId) => {
    setConfirmDialog({
      open: true,
      title: "Permanently Delete",
      message: "Are you sure you want to permanently delete this archived prayer? This cannot be undone.",
      onConfirm: () => {
        ArchiveManager.deletePermanently('special-prayer', devotionId);
        setArchivedList(ArchiveManager.getArchivedItems('special-prayer'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        if (closetDetailModalOpen) handleCloseClosetDetailModal();
        setToast({ open: true, message: "Item permanently removed.", severity: "success" });
      }
    });
  };

  const handleUnpublish = (devotion) => {
    setConfirmDialog({
      open: true,
      title: "Unpublish Special Prayer",
      message: `Are you sure you want to unpublish "${devotion.title || 'this prayer'}"? It will be removed from the mobile app and moved to the Unpublished tab.`,
      confirmText: "Unpublish",
      severity: "warning",
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        if (closetDetailModalOpen) handleCloseClosetDetailModal();
        try {
          setLoading(true);
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}unpublish-special-prayer`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: Number(devotion.id) })
          });
          const data = await response.json();
          if (response.ok && data.success !== false) {
            UnpublishedManager.addUnpublished('special-prayer', devotion);
            setUnpublishedList(UnpublishedManager.getUnpublishedIds('special-prayer'));
            setToast({ open: true, message: `"${devotion.title}" unpublished successfully and moved to Unpublished tab.`, severity: "warning" });
            setDevotionList(prev => prev.map(p => {
              if (Number(p.id) === Number(devotion.id)) {
                return { ...p, status: 'inactive' };
              }
              return p;
            }));
            setClosetCounts(prev => ({
              ...prev,
              active: Math.max(0, (prev.active || 0) - 1),
              unpublished: (prev.unpublished || 0) + 1,
            }));
          } else {
            setToast({ open: true, message: data.message || "Failed to unpublish prayer", severity: "error" });
          }
        } catch (e) {
          setToast({ open: true, message: "Network error unpublishing prayer", severity: "error" });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  useEffect(() => {
    fetchCategories();
    fetchTemplates();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}special-prayer-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ page: 1 })
      });
      const data = await response.json();
      if (data?.data?.organisations) {
        setOrganizations(data.data.organisations);
      }
      if (data?.data?.categories) {
        setCategoriesList(data.data.categories);
      }
    } catch (e) {
      console.error("Error fetching categories:", e);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}template-list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data?.data && Array.isArray(data.data)) {
        setTemplatesList(data.data);
      }
    } catch (e) {
      console.error("Error fetching templates:", e);
    }
  };

  const handleEdit = (id) => {
    navigate('/admin/edit-special-prayer/' + id)
  };

  useEffect(() => {
    const fetchClosetCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const orgParam = effectiveOrgId || (typeof organization === 'object' && organization !== null ? organization?.id : (organization && organization !== 'all' ? organization : null));

        // Active count (all live prayers)
        const resActive = await fetch(`${API_URL}special-prayer-list`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ organization: orgParam, page: 1, search: "", status: 'active' })
        });
        const dataActive = await resActive.json();
        const activeCount = typeof dataActive.total === 'number' ? dataActive.total : (dataActive.data?.length || 0);

        // Inactive count (contains both pending and unpublished)
        const resPending = await fetch(`${API_URL}special-prayer-list`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ organization: orgParam, page: 1, search: "", status: 'pending' })
        });
        const dataPending = await resPending.json();
        const inactiveItems = dataPending.data || [];
        const currentUnpublishedIds = UnpublishedManager.getUnpublishedIds('special-prayer');
        const unpubCount = inactiveItems.filter(d => currentUnpublishedIds.includes(String(d.id))).length;
        const pendingCountTotal = Math.max(0, (typeof dataPending.total === 'number' ? dataPending.total : inactiveItems.length) - unpubCount);

        // This Month count (active prayers this month)
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
        const resMonth = await fetch(`${API_URL}special-prayer-list`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ organization: orgParam, fromDate: firstDay, toDate: lastDay, page: 1, status: 'active' })
        });
        const dataMonth = await resMonth.json();
        const thisMonth = typeof dataMonth.total === 'number' ? dataMonth.total : (dataMonth.data?.length || 0);

        setClosetCounts({
          all: activeCount,
          active: activeCount,
          pending: pendingCountTotal,
          unpublished: unpubCount,
          thisMonth: thisMonth
        });
      } catch (err) {
        console.error("Error fetching closet counts:", err);
      }
    };
    fetchClosetCounts();
  }, [effectiveOrgId, organization, refreshTrigger]);

  useEffect(() => {
    const fetchSpecialPrayers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const orgParam = effectiveOrgId || (typeof organization === 'object' && organization !== null ? organization?.id : (organization && organization !== 'all' ? organization : null));
        const statusParam = (isPendingView || filterActiveTab === 'pending' || isUnpublishedView || filterActiveTab === 'unpublished')
          ? 'pending'
          : 'active';
        const response = await fetch(`${API_URL}special-prayer-list`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            search: search,
            organization: orgParam,
            category: selectedCategory && selectedCategory !== 'all' ? selectedCategory : undefined,
            page: page,
            fromDate: fromDate,
            toDate: toDate,
            status: statusParam
          })
        });

        if (!response.ok) {
          console.error("HTTP error fetching special prayers:", response.status);
          setLoading(false);
          return;
        }
        const data = await response.json();

        if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

        }
        setDevotionList(data.data || []);
        setTotalPages(data.totalPages || 1);
        setTotalCount(typeof data.total === 'number' ? data.total : (data.data?.length || 0));

      } catch (err) {
        // setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }

      //   setDevotionType(data.settings.hdailydevotiondefault === 'yes');
      //   setDevotionType(data.settings.hdailydevotiondefault)
    };
    fetchSpecialPrayers();
  }, [fromDate, toDate, organization, search, page, selectedCategory, filterActiveTab, refreshTrigger]);







  const handleEnableDisable = async (id, status) => {

    const token = localStorage.getItem('token');

    if (status === true) {
      if (!window.confirm("Are you sure you want to enable this Special Prayer?")) {
        return;
      }
    } else {
      if (!window.confirm("Are you sure you want to disable this Special Prayer?")) {
        return;
      }
    }

    const response = await fetch(`${API_URL}update-special-prayer-settings`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        // ❌ Do NOT set 'Content-Type' when sending FormData
      },
      body: JSON.stringify({
        id: id,
        status: status,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
      // 🔑 Handle invalid token
      if (data.message === 'Invalid token') {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }

      setToast({ open: true, message: data?.message || 'Failed to delete song', severity: "error" });
    }
    setToast({ open: true, message: "Settings updated successfully", severity: "success" });
    setTimeout(() => {
      window.location.reload();
    }, 1500);


  }

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Special Prayer',
      message: 'Are you sure you want to delete this prayer? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        if (closetDetailModalOpen) handleCloseClosetDetailModal();

        try {
          setLoading(true);
          const token = localStorage.getItem('token');

          const response = await fetch(`${API_URL}delete-special-prayer`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id }),
          });

          const data = await response.json();

          if (!response.ok || data.success === false) {
            // 🔑 Handle invalid token
            if (data.message === 'Invalid token') {
              localStorage.removeItem('token');
              window.location.href = '/login';
              return;
            }

            setToast({ open: true, message: data?.message || 'Failed to delete song', severity: "error" });
          }

          setToast({ open: true, message: "Special Prayer deleted successfully", severity: "success" });
          setTimeout(() => {
            window.location.reload();
          }, 1500);

        } catch (error) {
          console.error('Error deleting special prayer:', error);
          setToast({ open: true, message: "An error occurred while deleting the prayer", severity: "error" });
        } finally {
          setLoading(false);
        }
      }
    });
  };


  const [sortOrder, setSortOrder] = useState('asc');

  const pendingCount = useMemo(() => {
    if (typeof closetCounts.pending === 'number') return closetCounts.pending;
    return (devotionList || []).filter(d => (d.status === 'inactive' || d.status === 'pending' || d.status === 0 || d.status === '0') && !unpublishedList.includes(String(d.id)) && !archivedList.some(a => String(a.id) === String(d.id))).length;
  }, [closetCounts.pending, devotionList, unpublishedList, archivedList]);

  const handlePublish = (devotionOrId) => {
    const devotion = typeof devotionOrId === 'object' && devotionOrId !== null
      ? devotionOrId
      : (devotionList.find(d => Number(d.id) === Number(devotionOrId)) || { id: devotionOrId });
    const id = devotion.id;
    const title = devotion.title ? `"${devotion.title}"` : "this prayer";

    // Required fields check before letting admin submit to publish
    const missing = [];
    if (!devotion.title || !String(devotion.title).trim()) missing.push('Title');
    if (!devotion.category) missing.push('Category');
    const descText = devotion.description ? devotion.description.replace(/<[^>]+>/g, '').trim() : '';
    if (!descText) missing.push('Description');
    if (devotion.flow_type === 'fixed' || devotion.flow_type === 'open') {
      if (!devotion.from_date || !devotion.to_date) missing.push('Start & End Dates');
    } else if (devotion.flow_type === 'floating') {
      if (!devotion.number_of_days || Number(devotion.number_of_days) < 1) missing.push('Number of Days');
    }
    if (!devotion.from_time || !devotion.to_time) missing.push('Time Slots');

    if (missing.length > 0) {
      setToast({
        open: true,
        message: `Cannot publish: Missing required field(s): ${missing.join(', ')}. Please edit the prayer and complete all fields before publishing.`,
        severity: "error"
      });
      return;
    }

    setConfirmDialog({
      open: true,
      title: "Publish Special Prayer",
      message: `Are you sure you want to publish ${title} to the mobile app? It will become active and visible to all app users immediately.`,
      confirmText: "Publish",
      severity: "info",
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        if (closetDetailModalOpen) handleCloseClosetDetailModal();
        try {
          setLoading(true);
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}publish-special-prayer`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: Number(id) })
          });
          const data = await response.json();
          if (response.ok && data.success !== false) {
            UnpublishedManager.removeUnpublished('special-prayer', id);
            setUnpublishedList(UnpublishedManager.getUnpublishedIds('special-prayer'));
            setToast({ open: true, message: `Special prayer ${title} published to mobile app successfully!`, severity: "success" });
            setDevotionList(prev => prev.map(p => {
              if (Number(p.id) === Number(id)) {
                return { ...p, status: 'active' };
              }
              return p;
            }));
            setClosetCounts(prev => ({
              ...prev,
              active: (prev.active || 0) + 1,
              pending: Math.max(0, (prev.pending || 0) - 1),
              unpublished: Math.max(0, (prev.unpublished || 0) - 1)
            }));
          } else {
            setToast({ open: true, message: data.message || "Failed to publish prayer", severity: "error" });
          }
        } catch (e) {
          setToast({ open: true, message: "Network error publishing prayer", severity: "error" });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const displayedDevotions = useMemo(() => {
    let raw = (filterActiveTab === "archived" || isArchivedMode)
      ? archivedList
      : devotionList.filter(d => !archivedList.some(a => String(a.id) === String(d.id)));

    if (filterActiveTab === "archived" || isArchivedMode) {
      raw = archivedList;
    } else if (isUnpublishedView || filterActiveTab === "unpublished") {
      raw = raw.filter(d => (d.status === 'inactive' || d.status === 0 || d.status === '0') &&
        unpublishedList.includes(String(d.id))
      );
    } else if (isPendingView || filterActiveTab === "pending") {
      raw = raw.filter(d => (d.status === 'inactive' || d.status === 'pending' || d.status === 0 || d.status === '0') &&
        !unpublishedList.includes(String(d.id))
      );
    } else {
      raw = raw.filter(d => d.status === 'active' || d.status === 1 || d.status === '1');
    }

    return [...raw].sort((a, b) => {
      if (sortOrder === 'date_asc') return new Date(a.from_date || a.created_at || 0) - new Date(b.from_date || b.created_at || 0);
      if (sortOrder === 'date_desc') return new Date(b.from_date || b.created_at || 0) - new Date(a.from_date || a.created_at || 0);
      const nameA = (a.title || a.name || '').trim().toLowerCase();
      const nameB = (b.title || b.name || '').trim().toLowerCase();
      if (sortOrder === 'desc') return nameB.localeCompare(nameA);
      return nameA.localeCompare(nameB);
    });
  }, [isPendingView, isUnpublishedView, isArchivedMode, filterActiveTab, archivedList, unpublishedList, devotionList, sortOrder]);

  return (

    <>
      {!hideHeader && (
        <Helmet>
          <title>Special Prayers - GPN</title>
        </Helmet>
      )}
      <Box sx={{ p: hideHeader ? 0 : 4 }}>
        {!hideHeader && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Special Prayers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and offer focused prayers for special needs and occasions.
              </Typography>
            </Box>
          </Box>
        )}







        {/* Unified Toolbar for Prayers, or Clean Header for Pending Reviews / Unpublished / Archived */}
        {(isPendingView || isUnpublishedView || isArchivedView) ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              px: 0.5,
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  fontSize: "1.05rem",
                }}
              >
                {isArchivedView
                  ? "Archived Prayers"
                  : isUnpublishedView
                    ? "Unpublished Prayers"
                    : "Pending Reviews"}
              </Typography>
              <Chip
                label={`${displayedDevotions.length} Total`}
                size="small"
                sx={{
                  bgcolor: isArchivedView
                    ? (isLight ? "#FEF3C7" : "rgba(217, 119, 6, 0.2)")
                    : isUnpublishedView
                      ? (isLight ? "#FFEDD5" : "rgba(234, 88, 12, 0.2)")
                      : (isLight ? "#FEF3C7" : "rgba(217, 119, 6, 0.2)"),
                  color: isArchivedView
                    ? (isLight ? "#B45309" : "#FBBF24")
                    : isUnpublishedView
                      ? (isLight ? "#C2410C" : "#FB923C")
                      : (isLight ? "#B45309" : "#FBBF24"),
                  border: isLight ? "1.5px solid #FDE68A" : "1px solid rgba(217, 119, 6, 0.4)",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  height: 22,
                }}
              />
            </Box>

            <TextField
              size="small"
              placeholder="Search prayers..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: isLight ? "#0D9488" : "rgba(255, 255, 255, 0.5)", fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 240 },
                "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" },
              }}
            />
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
            backgroundColor: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.04)",
            border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.08)",
            display: "flex",
            flexDirection: "column",
            gap: 1.2,
            boxShadow: isLight ? "0 1px 3px rgba(15, 23, 42, 0.04)" : "none",
          }}
        >
          {/* Row 1: Title, Total, Search, Sort & Action Buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.2,
              pb: 1,
              borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255, 255, 255, 0.06)",
            }}
          >
            {/* Left: Title & Count Badge */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  fontSize: "1rem",
                }}
              >
                Prayer Closet
              </Typography>
              <Chip
                label={
                  filterActiveTab === "archived"
                    ? `${archivedList.length} Archived`
                    : filterActiveTab === "this_month"
                      ? `${closetCounts.thisMonth || 0} This Month`
                      : `${closetCounts.active || totalCount} Live`
                }
                size="small"
                sx={{
                  bgcolor: isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.15)",
                  color: isLight ? "#0D9488" : "#64d2e2",
                  border: isLight ? "1.5px solid #99F6E4" : "1px solid rgba(76, 161, 175, 0.35)",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  height: 22,
                }}
              />
            </Box>

            {/* Right: Search, Sort & Actions Group */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <TextField
                size="small"
                placeholder="Search prayers..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: isLight ? "#0D9488" : "rgba(255, 255, 255, 0.5)", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 190 },
                  "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" },
                }}
              />

              <Select
                size="small"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                displayEmpty
                renderValue={(val) => `Sort: ${val === "asc" ? "A → Z" : val === "desc" ? "Z → A" : "Date"}`}
                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
              >
                <MenuItem value="asc">Name: A → Z (Alphabetical)</MenuItem>
                <MenuItem value="desc">Name: Z → A</MenuItem>
                <MenuItem value="date_desc">Date: Newest First</MenuItem>
                <MenuItem value="date_asc">Date: Oldest First</MenuItem>
              </Select>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon sx={{ fontSize: 17 }} />}
                onClick={downloadSpecialPrayerTemplate}
                sx={{
                  borderColor: isLight ? "#0D9488" : "rgba(78, 141, 158, 0.5)",
                  color: isLight ? "#0D9488" : "#6FB4C6",
                  textTransform: "none",
                  borderRadius: "6px",
                  fontWeight: 600,
                  py: 0.5,
                  px: 1.2,
                  fontSize: "0.8rem",
                  "&:hover": { borderColor: isLight ? "#0F766E" : "#6FB4C6" },
                }}
              >
                Template
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon sx={{ fontSize: 17 }} />}
                onClick={() => setBulkDialogOpen(true)}
                sx={{
                  borderColor: isLight ? "#0F766E" : "rgba(23, 115, 115, 0.6)",
                  color: isLight ? "#0F766E" : "#E0F2F1",
                  textTransform: "none",
                  borderRadius: "6px",
                  fontWeight: 600,
                  py: 0.5,
                  px: 1.2,
                  fontSize: "0.8rem",
                  "&:hover": { borderColor: isLight ? "#115E59" : "#26A69A" },
                }}
              >
                Import
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DraftsIcon sx={{ fontSize: 17 }} />}
                onClick={() => {
                  setDraftsList(DraftManager.getDrafts("special-prayer", currentOrgKey));
                  setDraftsModalOpen(true);
                }}
                sx={{
                  borderColor: isLight ? "#D97706" : "rgba(232, 184, 75, 0.5)",
                  color: isLight ? "#B45309" : "#E8B84B",
                  textTransform: "none",
                  borderRadius: "6px",
                  fontWeight: 600,
                  py: 0.5,
                  px: 1.2,
                  fontSize: "0.8rem",
                  "&:hover": { borderColor: "#D97706" },
                }}
              >
                Drafts ({draftsList.length}/5)
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate("/admin/add-special-prayer")}
                sx={{
                  background: isLight ? "linear-gradient(135deg, #0D9488 0%, #0F766E 100%)" : "linear-gradient(135deg, #177373 0%, #208e8e 100%)",
                  color: "#FFFFFF",
                  textTransform: "none",
                  borderRadius: "6px",
                  fontWeight: 700,
                  py: 0.6,
                  px: 1.5,
                  fontSize: "0.82rem",
                  "&:hover": { background: "#0F766E" },
                }}
              >
                Add Prayer Closet
              </Button>
            </Box>
          </Box>

          {/* Row 2: Status Triage Chips + Date & Org Filters */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Left: Quick Status Chips */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
              {[
                { key: "active", label: "Active", count: closetCounts.active ?? totalCount },
                { key: "pending", label: "Pending Reviews", count: closetCounts.pending ?? pendingCount, isPending: true },
                { key: "unpublished", label: "Unpublished", count: closetCounts.unpublished ?? unpublishedList.length, isUnpublished: true },
                { key: "archived", label: "Archived", count: archivedList.length, isArchived: true },
                { key: "this_month", label: "This Month", count: closetCounts.thisMonth ?? 0 },
              ].map((item) => {
                const isActive = filterActiveTab === item.key;
                return (
                  <Chip
                    key={item.key}
                    size="small"
                    clickable
                    label={`${item.label} (${item.count})`}
                    onClick={() => {
                      if (item.key === "pending") {
                        if (onNavigateTab) {
                          onNavigateTab("pending");
                        } else {
                          navigate("/admin/prayer-closet?tab=pending");
                        }
                        return;
                      }
                      if (item.key === "unpublished") {
                        if (onNavigateTab) {
                          onNavigateTab("unpublished");
                        } else {
                          navigate("/admin/prayer-closet?tab=unpublished");
                        }
                        return;
                      }
                      if (item.key === "archived") {
                        if (onNavigateTab) {
                          onNavigateTab("archived");
                        } else {
                          navigate("/admin/prayer-closet?tab=archived");
                        }
                        return;
                      }
                      if (item.key === "this_month") {
                        if (filterActiveTab === "this_month") {
                          setFilterActiveTab("active");
                          setFromDate("");
                          setToDate("");
                        } else {
                          setFilterActiveTab("this_month");
                          const now = new Date();
                          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                          const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
                          setFromDate(firstDay);
                          setToDate(lastDay);
                        }
                      } else {
                        setFilterActiveTab(item.key);
                        setFromDate("");
                        setToDate("");
                      }
                      setPage(1);
                    }}
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: isActive ? 700 : 500,
                      bgcolor: isActive
                        ? (item.isUnpublished ? "#EA580C" : item.isPending ? "#D97706" : item.isArchived ? "#D97706" : "#0F766E")
                        : (item.isUnpublished && item.count > 0
                            ? (isLight ? "#FFEDD5" : "rgba(234, 88, 12, 0.2)")
                            : item.isPending && item.count > 0
                              ? (isLight ? "#FEF3C7" : "rgba(217, 119, 6, 0.2)")
                              : item.isArchived && item.count > 0
                                ? (isLight ? "#FEF3C7" : "rgba(217, 119, 6, 0.2)")
                                : (isLight ? "#F1F5F9" : "rgba(255, 255, 255, 0.08)")),
                      color: isActive
                        ? "#FFFFFF"
                        : (item.isUnpublished && item.count > 0
                            ? (isLight ? "#C2410C" : "#FB923C")
                            : item.isPending && item.count > 0
                              ? (isLight ? "#B45309" : "#FBBF24")
                              : item.isArchived && item.count > 0
                                ? (isLight ? "#92400E" : "#FBBF24")
                                : (isLight ? "#475569" : "rgba(255, 255, 255, 0.8)")),
                      height: 26,
                      "&:hover": {
                        bgcolor: isActive
                          ? (item.isUnpublished ? "#C2410C" : item.isPending ? "#B45309" : item.isArchived ? "#B45309" : "#115E59")
                          : (isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.15)")
                      },
                    }}
                  />
                );
              })}

              <Chip
                size="small"
                clickable
                label={`Categories (${categoriesList.length})`}
                onClick={() => {
                  if (onNavigateTab) {
                    onNavigateTab('categories');
                  } else {
                    navigate('/admin/prayer-closet?tab=categories');
                  }
                }}
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 500,
                  bgcolor: isLight ? "#E0F2FE" : "rgba(2, 132, 199, 0.15)",
                  color: isLight ? "#0369A1" : "#38BDF8",
                  height: 26,
                  "&:hover": { bgcolor: isLight ? "#BAE6FD" : "rgba(2, 132, 199, 0.25)" },
                }}
              />
            </Box>

            {/* Right: Dates, Category, Org, Reset */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
              <Select
                size="small"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setPage(1);
                }}
                displayEmpty
                renderValue={(val) => {
                  if (!val || val === 'all') return 'All Categories';
                  const found = categoriesList.find(c => String(c.id) === String(val));
                  return found ? `Category: ${found.name}` : 'Category';
                }}
                sx={{
                  width: 145,
                  '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' }
                }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categoriesList.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                label="Start"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => {
                  if (e.target.value.length <= 10) setFromDate(e.target.value);
                  setPage(1);
                }}
                inputProps={{ max: "9999-12-31" }}
                sx={{
                  width: 130,
                  "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" },
                }}
              />

              <TextField
                size="small"
                label="End"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => {
                  if (e.target.value.length <= 10) setToDate(e.target.value);
                  setPage(1);
                }}
                inputProps={{ max: "9999-12-31" }}
                sx={{
                  width: 130,
                  "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" },
                }}
              />

              {showOrganizationColumn && (
                <Autocomplete
                  size="small"
                  value={organization}
                  onChange={(event, newValue) => {
                    setOrganization(newValue);
                    setPage(1);
                  }}
                  options={orgOptions}
                  getOptionLabel={(option) => option?.org_name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Org"
                      sx={{ "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
                    />
                  )}
                  sx={{ width: 160 }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}

              {(search || fromDate || toDate || (organization && organization.id !== 0)) && (
                <Button
                  size="small"
                  onClick={() => {
                    setSearch("");
                    setFromDate("");
                    setToDate("");
                    setSelectedCategory("all");
                    setOrganization(orgOptions[0] || null);
                    setPage(1);
                  }}
                  sx={{
                    color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)",
                    textTransform: "none",
                    fontSize: "0.78rem",
                    borderRadius: "6px",
                    px: 1,
                    py: 0.4,
                    "&:hover": {
                      color: "#EF4444",
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
        )}

        {/* DEVOTIONS TABLE / LIST VIEW */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: "16px",
            overflowX: { xs: "hidden", lg: "auto" },
            maxWidth: "100%",
            border: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(76, 161, 175, 0.25)",
            background: isLight ? "#FFFFFF" : "rgba(21, 34, 46, 0.7)",
            boxShadow: isLight ? "0 4px 16px rgba(15, 23, 42, 0.05)" : "none",
            mb: 3,
          }}
        >
          {loading ? (
            <Box py={8}>
              <SectionLoader />
            </Box>
          ) : displayedDevotions.length === 0 ? (
            <Box py={8} textAlign="center">
              <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                {isArchivedView
                  ? "No Archived Prayers Found"
                  : isUnpublishedView
                    ? "No Unpublished Prayers Found"
                    : isPendingView
                      ? "No Pending Prayers for Review"
                      : "No Special Prayers Found"}
              </Typography>
              <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", mt: 0.5 }}>
                {isArchivedView
                  ? "Archived prayers will appear here."
                  : isUnpublishedView
                    ? "Prayers that are unpublished from the mobile app will appear here."
                    : isPendingView
                      ? "All prayers are currently reviewed and active."
                      : "Try adjusting your search terms, date range, or selected filter tab."}
              </Typography>
            </Box>
          ) : (
            <Table size="small" sx={{ width: "100%", minWidth: 0, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: isLight ? "#F1F5F9" : "#1a2636",
                      color: isLight ? "#1E293B" : "#F8FAFC",
                      fontWeight: 800,
                      fontSize: "0.78rem",
                      letterSpacing: "0.6px",
                      textTransform: "uppercase",
                      borderBottom: isLight ? "2px solid #CBD5E1" : "1.5px solid rgba(255,255,255,0.12)",
                      py: 1.5,
                    }
                  }}
                >
                  <TableCell align="center" sx={{ width: { xs: 36, sm: 46 } }}>#</TableCell>
                  <TableCell sx={{ width: { xs: 65, sm: 80 } }}>Banner</TableCell>
                  <TableCell sx={{ minWidth: 0 }}>Prayer Details</TableCell>
                  {showOrganizationColumn && <TableCell className="col-tablet-hide" sx={{ display: { xs: 'none', lg: 'table-cell' }, width: 140 }}>Organization</TableCell>}
                  <TableCell className="col-tablet-hide" sx={{ display: { xs: 'none', lg: 'table-cell' }, width: 140 }}>Format & Duration</TableCell>
                  <TableCell className="col-tablet-hide-portrait" sx={{ display: { xs: 'none', md: 'none', lg: 'table-cell' }, width: 110 }}>Date</TableCell>
                  <TableCell sx={{ width: { xs: 80, sm: 95 } }}>Status</TableCell>
                  <TableCell align="center" sx={{ width: { xs: 110, sm: 125, md: 140 } }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {displayedDevotions.map((devotion, index) => {
                  const statusBg = devotion.deleted_at
                    ? "#EF4444"
                    : devotion.status === "inactive"
                      ? "#F59E0B"
                      : "#10B981";
                  const statusLabel = devotion.deleted_at ? "Deleted" : devotion.status === "inactive" ? "Inactive" : "Active";
                  const imageUrl = devotion.image && devotion.image.trim() !== '' ? devotion.image : '/assets/images/no-image-placeholder.png';
                  const plainDesc = stripHtml(devotion.description || "");

                  return (
                    <TableRow
                      key={devotion.id || index}
                      hover
                      onClick={() => handleViewPrayerCloset(devotion)}
                      sx={{
                        cursor: "pointer",
                        transition: "background-color 0.15s ease",
                        '&:hover': {
                          bgcolor: isLight ? "#F0FDFA !important" : "rgba(13, 148, 136, 0.1) !important",
                        },
                        '& td': {
                          borderBottom: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.06)",
                          py: 1.35,
                          color: isLight ? "#1E293B" : "#F1F5F9",
                        }
                      }}
                    >
                      {/* Index */}
                      <TableCell align="center" sx={{ fontWeight: 700, color: isLight ? "#64748B" : "rgba(255,255,255,0.5)", fontSize: "0.82rem" }}>
                        {((page - 1) * 9) + index + 1}
                      </TableCell>

                      {/* Banner Image Thumbnail */}
                      <TableCell>
                        <Avatar
                          variant="rounded"
                          src={imageUrl}
                          sx={{
                            width: 64,
                            height: 44,
                            borderRadius: "8px",
                            border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255,255,255,0.15)",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                          }}
                        />
                      </TableCell>

                      {/* Prayer Details: Title & Description */}
                      <TableCell>
                        <Box sx={{ pr: 1 }}>
                          <Box display="flex" alignItems="center" gap={0.8} mb={0.4} flexWrap="wrap">
                            <Typography
                              variant="body2"
                              fontWeight={800}
                              sx={{
                                color: isLight ? "#0D9488" : "#5EEAD4",
                                fontSize: "0.92rem",
                                lineHeight: 1.25,
                              }}
                            >
                              {devotion.title || "Untitled Prayer"}
                            </Typography>
                            {(devotion.prayer_type || devotion.pray_type) && (
                              <Chip
                                size="small"
                                label={devotion.prayer_type || devotion.pray_type}
                                sx={{
                                  height: 20,
                                  fontSize: "0.68rem",
                                  fontWeight: 700,
                                  borderRadius: "6px",
                                  bgcolor: (devotion.prayer_type || devotion.pray_type) === "Personal" ? "rgba(147, 51, 234, 0.15)" : "rgba(14, 116, 144, 0.15)",
                                  color: (devotion.prayer_type || devotion.pray_type) === "Personal" ? "#9333EA" : "#0284C7",
                                  border: "1px solid rgba(255,255,255,0.1)",
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isLight ? "#475569" : "rgba(255, 255, 255, 0.7)",
                              fontSize: "0.82rem",
                              lineHeight: 1.35,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {plainDesc || "No additional description provided."}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Organization */}
                      {showOrganizationColumn && (
                        <TableCell className="col-tablet-hide" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Box display="flex" alignItems="center" gap={0.8}>
                            <BusinessIcon sx={{ fontSize: 16, color: isLight ? "#64748B" : "rgba(255,255,255,0.4)" }} />
                            <Typography
                              variant="body2"
                              noWrap
                              fontWeight={600}
                              sx={{ color: isLight ? "#334155" : "#E2E8F0", fontSize: "0.82rem" }}
                            >
                              {devotion.origanisation?.org_name || devotion.organizations?.org_name || "Global / General"}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}

                      {/* Format & Duration */}
                      <TableCell className="col-tablet-hide" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Box display="flex" flexDirection="column" gap={0.4}>
                          <Chip
                            size="small"
                            label={devotion.prayer_plan ? String(devotion.prayer_plan) : "Standard"}
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              textTransform: "capitalize",
                              width: "fit-content",
                              bgcolor: isLight ? "#F1F5F9" : "rgba(255, 255, 255, 0.08)",
                              color: isLight ? "#334155" : "rgba(255, 255, 255, 0.8)",
                            }}
                          />
                          <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: "0.75rem" }}>
                            {devotion.no_of_days ? `${devotion.no_of_days} Days` : devotion.start_date ? `${devotion.start_date}` : "Ongoing"}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Date */}
                      <TableCell className="col-tablet-hide-portrait" sx={{ display: { xs: 'none', md: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                          {new Date(devotion.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusLabel}
                          sx={{
                            height: 22,
                            fontSize: "0.72rem",
                            fontWeight: 800,
                            textTransform: "uppercase",
                            bgcolor: statusBg,
                            color: "#FFFFFF",
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ width: { xs: 110, sm: 125, md: 140 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8 }}>
                          {/* Main Action 1: Edit button (Always visible) */}
                          <Tooltip title="Edit Prayer">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(devotion.id)}
                              sx={{
                                color: isLight ? "#0F766E" : "#64D2E2",
                                bgcolor: isLight ? "rgba(15, 118, 110, 0.08)" : "rgba(76, 161, 175, 0.15)",
                                border: isLight ? "1px solid rgba(15, 118, 110, 0.2)" : "1px solid rgba(76, 161, 175, 0.25)",
                                '&:hover': { bgcolor: isLight ? "rgba(15, 118, 110, 0.18)" : "rgba(76, 161, 175, 0.3)" }
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          {/* Main Action 2: Quick Publish button if inactive */}
                          {(devotion.status === 'inactive' || devotion.status === 'pending' || devotion.status === 0 || devotion.status === '0') && (
                            <Tooltip title="Publish to mobile app">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handlePublish(devotion)}
                                sx={{
                                  fontSize: '0.72rem',
                                  py: 0.25,
                                  px: 1.1,
                                  fontWeight: 800,
                                  bgcolor: '#0F766E',
                                  color: '#FFFFFF',
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  boxShadow: 'none',
                                  '&:hover': { bgcolor: '#115E59' }
                                }}
                              >
                                Publish
                              </Button>
                            </Tooltip>
                          )}

                          {/* Main Action 3: 3 Dots Menu Button - Opens details modal popup */}
                          <Tooltip title="View Details & More Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewPrayerCloset(devotion);
                              }}
                              sx={{
                                color: isLight ? "#334155" : "#E2E8F0",
                                bgcolor: isLight ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.08)",
                                border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)",
                                borderRadius: "8px",
                                p: 0.5,
                                '&:hover': {
                                  bgcolor: isLight ? "rgba(15, 118, 110, 0.15)" : "rgba(76, 161, 175, 0.25)",
                                  color: isLight ? "#0F766E" : "#5EEAD4",
                                }
                              }}
                            >
                              <MoreVertIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {totalPages > 0 && (
          <Box display="flex" justifyContent="center" mt={3} width="100%">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
            />
          </Box>
        )}


      </Box>
      <Toast toast={toast} setToast={setToast} />
      {/* Floating Settings Icon (only when accessed directly without hub header) */}
      {!hideHeader && (
        <>
          <Tooltip title="Special Prayers Settings" placement="left">
            <IconButton
              onClick={() => setSettingsOpen(true)}
              sx={{
                position: 'fixed',
                bottom: 32,
                right: 32,
                bgcolor: 'rgba(76, 161, 175, 0.15)',
                border: '1px solid rgba(76, 161, 175, 0.4)',
                color: '#64d2e2',
                width: 48,
                height: 48,
                zIndex: 1300,
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: 'rgba(76, 161, 175, 0.3)', transform: 'rotate(20deg)' },
                transition: 'all 0.25s ease',
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <ModuleSettingsModal
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            moduleKey="special-prayer"
            moduleTitle="Special Prayers"
          />
        </>
      )}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        confirmText={confirmDialog.confirmText || "Confirm"}
        severity={confirmDialog.severity || "warning"}
      />
      <DraftsModal
        open={draftsModalOpen}
        onClose={() => setDraftsModalOpen(false)}
        moduleName="Special Prayer"
        drafts={draftsList}
        onContinueEditing={(draft) => {
          setDraftsModalOpen(false);
          navigate("/admin/add-special-prayer", { state: { draft } });
        }}
        onDeleteDraft={(draftId) => {
          const res = DraftManager.deleteDraft('special-prayer', draftId, currentOrgKey);
          setDraftsList(res.drafts || []);
          setToast({ open: true, message: "Draft removed.", severity: "info" });
        }}
        onClearAllDrafts={() => {
          DraftManager.clearAllDrafts('special-prayer', currentOrgKey);
          setDraftsList([]);
          setToast({ open: true, message: "All drafts cleared.", severity: "info" });
        }}
      />
      <BulkSpecialPrayerDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        categories={categoriesList}
        templates={templatesList}
        API_URL={API_URL}
        onSuccess={(count) => {
          setToast({ open: true, message: `Successfully imported ${count} special prayer(s)!`, severity: 'success' });
          setBulkDialogOpen(false);
          setRefreshTrigger(prev => prev + 1);
        }}
      />
      {/* PRAYER CLOSET ENTRY DETAIL VIEW MODAL */}
      <Dialog
        open={closetDetailModalOpen}
        onClose={handleCloseClosetDetailModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: isLight ? {
            background: '#FFFFFF',
            borderRadius: '20px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.16)',
            color: '#0F172A',
            overflow: 'hidden',
          } : {
            background: 'linear-gradient(145deg, #15222E 0%, #1A2E3B 100%)',
            borderRadius: '20px',
            border: '1px solid rgba(76, 161, 175, 0.35)',
            boxShadow: '0 24px 60px rgba(0, 0, 0, 0.6)',
            color: '#FFFFFF',
            backdropFilter: 'blur(20px)',
            overflow: 'hidden',
          }
        }}
      >
        {selectedClosetEntry && (() => {
          const devotion = selectedClosetEntry;
          const statusText = devotion.deleted_at
            ? "Deleted"
            : devotion.status === "inactive"
              ? "Inactive"
              : "Active";

          const statusColor = devotion.deleted_at
            ? { bg: isLight ? "#FEE2E2" : "rgba(239,68,68,0.2)", border: isLight ? "#FCA5A5" : "rgba(239,68,68,0.5)", text: isLight ? "#DC2626" : "#F87171" }
            : devotion.status === "inactive"
              ? { bg: isLight ? "#FEF3C7" : "rgba(234,179,8,0.2)", border: isLight ? "#FDE68A" : "rgba(234,179,8,0.5)", text: isLight ? "#D97706" : "#FBBF24" }
              : { bg: isLight ? "#D1FAE5" : "rgba(16,185,129,0.2)", border: isLight ? "#A7F3D0" : "rgba(16,185,129,0.5)", text: isLight ? "#059669" : "#34D399" };

          const bgImg = devotion.image && devotion.image.trim() !== ''
            ? devotion.image
            : '/assets/images/no-image-placeholder.png';

          return (
            <>
              {/* Header with Background Banner */}
              <Box
                sx={{
                  position: "relative",
                  minHeight: "180px",
                  backgroundImage: `url(${bgImg})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  display: "flex",
                  alignItems: "flex-end",
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(to bottom, rgba(15,23,42,0.4) 0%, rgba(15,23,42,0.95) 100%)",
                  }}
                />

                <IconButton
                  size="small"
                  onClick={handleCloseClosetDetailModal}
                  sx={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    color: "rgba(255,255,255,0.8)",
                    bgcolor: "rgba(0,0,0,0.5)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.8)", color: "#fff" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>

                <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.6,
                        px: 1.4,
                        py: 0.4,
                        borderRadius: "20px",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        bgcolor: statusColor.bg,
                        border: `1px solid ${statusColor.border}`,
                        color: statusColor.text,
                      }}
                    >
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: statusColor.text }} />
                      {statusText}
                    </Box>

                    {/* Prayer Type Chip */}
                    {(devotion.prayer_type || devotion.pray_type) && (
                      <Chip
                        icon={<GroupIcon sx={{ fontSize: "14px !important", color: (devotion.prayer_type || devotion.pray_type) === "Personal" ? "#C084FC !important" : "#64d2e2 !important" }} />}
                        label={`${devotion.prayer_type || devotion.pray_type} Prayer`}
                        size="small"
                        sx={{
                          bgcolor: (devotion.prayer_type || devotion.pray_type) === "Personal" ? "rgba(168, 85, 247, 0.25)" : "rgba(76, 161, 175, 0.25)",
                          color: (devotion.prayer_type || devotion.pray_type) === "Personal" ? "#C084FC" : "#64d2e2",
                          border: `1px solid ${(devotion.prayer_type || devotion.pray_type) === "Personal" ? "rgba(168, 85, 247, 0.45)" : "rgba(76, 161, 175, 0.45)"}`,
                          fontWeight: 700,
                          fontSize: "0.72rem"
                        }}
                      />
                    )}

                    {(devotion.session_prayer_category || devotion.category_name) && (
                      <Chip
                        label={devotion.session_prayer_category?.name || devotion.session_prayer_category?.category_name || devotion.category_name || "Special Prayer"}
                        size="small"
                        sx={{
                          bgcolor: "rgba(76, 161, 175, 0.25)",
                          color: "#64d2e2",
                          border: "1px solid rgba(76, 161, 175, 0.4)",
                          fontWeight: 700,
                          fontSize: "0.72rem"
                        }}
                      />
                    )}

                    {showOrganizationColumn && devotion.origanisation?.org_name && (
                      <Chip
                        label={devotion.origanisation.org_name}
                        size="small"
                        sx={{
                          bgcolor: "rgba(255,255,255,0.12)",
                          color: "rgba(255,255,255,0.9)",
                          border: "1px solid rgba(255,255,255,0.2)",
                          fontSize: "0.72rem"
                        }}
                      />
                    )}

                    {/* Interactive User Activity & Warriors Chip */}
                    <Chip
                      icon={<TimelineIcon sx={{ fontSize: "14px !important", color: "#64d2e2 !important" }} />}
                      label="User Activity & Warriors"
                      clickable
                      onClick={() => {
                        setSelectedClosetForActivity(devotion);
                        setUserActivityModalOpen(true);
                      }}
                      size="small"
                      sx={{
                        bgcolor: "rgba(13, 148, 136, 0.25)",
                        color: "#5EEAD4",
                        border: "1px solid rgba(13, 148, 136, 0.5)",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "rgba(13, 148, 136, 0.45)" }
                      }}
                    />
                  </Box>

                  <Typography variant="h5" fontWeight={800} sx={{ color: "#FFFFFF", textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
                    {devotion.title}
                  </Typography>
                </Box>
              </Box>

              <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Description */}
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "14px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(0, 0, 0, 0.22)",
                    border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.2)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: isLight ? '#0D9488' : '#4CA1AF', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 800, display: "block", mb: 0.8 }}
                  >
                    Prayer Closet Overview & Instructions
                  </Typography>

                  <Box
                    sx={{
                      color: isLight ? "#334155" : "rgba(255, 255, 255, 0.9)",
                      lineHeight: 1.7,
                      fontSize: "0.95rem",
                      "& p": { m: 0, mb: 1, "&:last-child": { mb: 0 } },
                      "& strong, & b": { color: isLight ? "#0F172A" : "#FFFFFF", fontWeight: 700 },
                      "& ul, & ol": { pl: 2.5, my: 1 },
                      "& li": { mb: 0.5 },
                      "& a": { color: isLight ? "#0D9488" : "#64d2e2" }
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(devotion.description || "No description provided for this prayer closet entry.")
                    }}
                  />
                </Box>

                {/* Schedule & Timing Info */}
                <Box
                  sx={{
                    p: 2.2,
                    borderRadius: "14px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(0, 0, 0, 0.22)",
                    border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.2)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: isLight ? '#0D9488' : '#4CA1AF', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 800, display: "block", mb: 1.2 }}
                  >
                    Timing & Schedule
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <GroupIcon sx={{ fontSize: 18, color: isLight ? "#0D9488" : "#64d2e2" }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Prayer Type</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", textTransform: "capitalize" }}>
                            {devotion.prayer_type || devotion.pray_type ? `${devotion.prayer_type || devotion.pray_type} Prayer` : "Group Prayer"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarTodayIcon sx={{ fontSize: 18, color: isLight ? "#0D9488" : "#64d2e2" }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Campaign Start</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                            {formatDate(devotion.from_date || devotion.start_date)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarMonthIcon sx={{ fontSize: 18, color: isLight ? "#0D9488" : "#64d2e2" }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Campaign End</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                            {formatDate(devotion.to_date || devotion.end_date)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccessTimeIcon sx={{ fontSize: 18, color: isLight ? "#0D9488" : "#64d2e2" }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Prayer Slots</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                            {devotion.timeslots?.length ? `${devotion.timeslots.length} slots configured` : devotion.time_slots ? formatTimeSlot(devotion.time_slots) : "All-day / Ongoing"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Scriptures Section if present */}
                {(devotion.session_scriptures?.length > 0 || devotion.scriptures?.length > 0) && (
                  <Box
                    sx={{
                      p: 2.2,
                      borderRadius: "14px",
                      bgcolor: isLight ? "#F8FAFC" : "rgba(0, 0, 0, 0.22)",
                      border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.2)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: isLight ? '#0D9488' : '#4CA1AF', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 800, display: "flex", alignItems: "center", gap: 0.8, mb: 1.2 }}
                    >
                      <MenuBookIcon sx={{ fontSize: 16 }} />
                      Guiding Scriptures
                    </Typography>

                    <Stack spacing={1}>
                      {(devotion.session_scriptures || devotion.scriptures).map((sc, idx) => (
                        <Box
                          key={sc.id || idx}
                          sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            bgcolor: isLight ? "#F1F5F9" : "rgba(76, 161, 175, 0.08)",
                            borderLeft: `3px solid ${isLight ? '#0D9488' : '#4CA1AF'}`,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: isLight ? "#1E293B" : "#E0E7FF", fontStyle: "italic" }}>
                            "{sc.scripture || sc.verse || sc.text || sc}"
                          </Typography>
                          {sc.reference && (
                            <Typography variant="caption" sx={{ color: isLight ? "#0D9488" : "#64d2e2", fontWeight: 700, display: "block", mt: 0.5 }}>
                              — {sc.reference}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Prayer Points Section if present */}
                {(devotion.session_prayer_points?.length > 0 || devotion.prayerpoints?.length > 0) && (
                  <Box
                    sx={{
                      p: 2.2,
                      borderRadius: "14px",
                      bgcolor: isLight ? "#F8FAFC" : "rgba(0, 0, 0, 0.22)",
                      border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.2)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: isLight ? '#0D9488' : '#4CA1AF', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 800, display: "flex", alignItems: "center", gap: 0.8, mb: 1.2 }}
                    >
                      <VolunteerActivismIcon sx={{ fontSize: 16 }} />
                      Key Prayer Points
                    </Typography>

                    <Stack spacing={1}>
                      {(devotion.session_prayer_points || devotion.prayerpoints).map((pp, idx) => (
                        <Box
                          key={pp.id || idx}
                          sx={{
                            p: 1.2,
                            borderRadius: "10px",
                            bgcolor: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.03)",
                            border: isLight ? "1px solid #E2E8F0" : "none",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.2,
                          }}
                        >
                          <Chip
                            label={idx + 1}
                            size="small"
                            sx={{
                              bgcolor: isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.2)",
                              color: isLight ? "#0D9488" : "#64d2e2",
                              fontWeight: 800,
                              height: 22,
                              minWidth: 22,
                              fontSize: "0.72rem"
                            }}
                          />
                          <Typography variant="body2" sx={{ color: isLight ? "#334155" : "rgba(255, 255, 255, 0.85)", pt: 0.2 }}>
                            {stripHtml(pp.prayer_point || pp.point || pp.title || pp)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1.2} flexWrap="wrap">
                  {devotion?.status === 'active' && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityOffIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleUnpublish(devotion)}
                      sx={{
                        color: isLight ? "#D97706" : "#FBBF24",
                        borderColor: isLight ? "#F59E0B" : "rgba(245, 158, 11, 0.5)",
                        bgcolor: isLight ? "#FFFBEB" : "rgba(245, 158, 11, 0.12)",
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': {
                          borderColor: '#D97706',
                          bgcolor: isLight ? '#FEF3C7' : 'rgba(245, 158, 11, 0.25)',
                          color: '#FFFFFF'
                        }
                      }}
                    >
                      Unpublish
                    </Button>
                  )}
                  {(devotion?.status === 'inactive' || devotion?.status === 'pending' || devotion?.status === 0 || devotion?.status === '0') && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        handlePublish(devotion);
                        handleCloseClosetDetailModal();
                      }}
                      sx={{
                        bgcolor: '#0F766E',
                        color: '#FFFFFF',
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': { bgcolor: '#115E59' }
                      }}
                    >
                      Publish to Mobile App
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PhoneIphoneIcon sx={{ fontSize: 16 }} />}
                    onClick={() => handleOpenMobilePreview(devotion)}
                    sx={{
                      color: isLight ? '#0D9488' : '#64d2e2',
                      borderColor: isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.4)',
                      bgcolor: isLight ? '#F0FDFA' : 'transparent',
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { borderColor: '#0D9488', bgcolor: isLight ? '#CCFBF1' : 'rgba(76, 161, 175, 0.15)' }
                    }}
                  >
                    User Preview
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      handleCloseClosetDetailModal();
                      handleEdit(devotion.id);
                    }}
                    sx={{
                      color: isLight ? '#0D9488' : '#64d2e2',
                      borderColor: isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.4)',
                      bgcolor: isLight ? '#F0FDFA' : 'transparent',
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { borderColor: '#0D9488', bgcolor: isLight ? '#CCFBF1' : 'rgba(76, 161, 175, 0.15)' }
                    }}
                  >
                    Edit Prayer
                  </Button>

                  {/* Archive button */}
                  {filterActiveTab !== "archived" && !archivedList.some(a => String(a.id) === String(devotion.id)) ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleArchive(devotion)}
                      startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        color: isLight ? "#D97706" : "#ffb74d",
                        borderColor: isLight ? "#FDE68A" : "rgba(255, 183, 77, 0.6)",
                        bgcolor: isLight ? "#FFFBEB" : "rgba(255, 183, 77, 0.12)",
                        textTransform: "none",
                        borderRadius: "10px",
                        fontWeight: 700,
                        "&:hover": { borderColor: "#ffb74d", bgcolor: "rgba(255, 183, 77, 0.25)", color: "#FFFFFF" }
                      }}
                    >
                      Archive
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          handleRestore(devotion.id);
                          handleCloseClosetDetailModal();
                        }}
                        startIcon={<UnarchiveIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          color: "#4CAF50",
                          borderColor: "rgba(76, 175, 80, 0.6)",
                          bgcolor: "rgba(76, 175, 80, 0.12)",
                          textTransform: "none",
                          borderRadius: "10px",
                          fontWeight: 700,
                          "&:hover": { borderColor: "#4CAF50", bgcolor: "rgba(76, 175, 80, 0.25)", color: "#FFFFFF" }
                        }}
                      >
                        Restore
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handlePermanentDelete(devotion.id)}
                        startIcon={<DeleteForeverIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          color: "#EF4444",
                          borderColor: "rgba(239, 68, 68, 0.6)",
                          bgcolor: "rgba(239, 68, 68, 0.12)",
                          textTransform: "none",
                          borderRadius: "10px",
                          fontWeight: 700,
                          "&:hover": { borderColor: "#EF4444", bgcolor: "rgba(239, 68, 68, 0.25)", color: "#FFFFFF" }
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  )}

                  {/* Direct Delete button for active prayers if owner/superadmin */}
                  {filterActiveTab !== "archived" && devotion.status !== "deleted" &&
                    (user?.role === 1 || Number(user?.org_id) === Number(devotion?.org_id)) && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDelete(devotion.id)}
                        startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          color: "#EF4444",
                          borderColor: "rgba(239, 68, 68, 0.6)",
                          bgcolor: "rgba(239, 68, 68, 0.12)",
                          textTransform: "none",
                          borderRadius: "10px",
                          fontWeight: 700,
                          "&:hover": { borderColor: "#EF4444", bgcolor: "rgba(239, 68, 68, 0.25)", color: "#FFFFFF" }
                        }}
                      >
                        Delete
                      </Button>
                    )}

                  {/* View User Activity Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TimelineIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      setSelectedClosetForActivity(devotion);
                      setUserActivityModalOpen(true);
                    }}
                    sx={{
                      color: isLight ? '#0D9488' : '#64D2E2',
                      borderColor: isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.45)',
                      bgcolor: isLight ? 'rgba(13, 148, 136, 0.08)' : 'rgba(76, 161, 175, 0.12)',
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 2,
                      '&:hover': {
                        borderColor: '#0D9488',
                        bgcolor: isLight ? 'rgba(13, 148, 136, 0.18)' : 'rgba(76, 161, 175, 0.25)',
                        color: isLight ? '#0F766E' : '#FFFFFF'
                      }
                    }}
                  >
                    User Activity & Warriors
                  </Button>
                </Box>

                <Button
                  onClick={handleCloseClosetDetailModal}
                  variant="outlined"
                  sx={{
                    color: isLight ? '#64748B' : '#4CA1AF',
                    borderColor: isLight ? '#CBD5E1' : 'rgba(76, 161, 175, 0.4)',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': { borderColor: isLight ? '#0D9488' : '#4CA1AF', bgcolor: isLight ? '#F1F5F9' : 'rgba(76, 161, 175, 0.12)' },
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* View User Activity Modal for this Prayer Closet */}
      <ViewUserActivityModal
        open={userActivityModalOpen}
        onClose={() => {
          setUserActivityModalOpen(false);
          setSelectedClosetForActivity(null);
        }}
        contentType="special_prayer"
        contentId={selectedClosetForActivity?.id}
        contentTitle={selectedClosetForActivity?.title}
        item={selectedClosetForActivity}
      />

      {/* Mobile App Preview Modal */}
      <MobilePrayerPreviewModal
        open={mobilePreviewOpen}
        onClose={() => {
          setMobilePreviewOpen(false);
          setSelectedPreviewPrayer(null);
        }}
        prayerTitle={selectedPreviewPrayer?.title || selectedPreviewPrayer?.name || "Pray for Christians"}
        dayNumber={1}
        totalDays={selectedPreviewPrayer?.number_of_days || 21}
        dayData={selectedPreviewPrayer?.days?.[0] || {}}
        prayerPoints={selectedPreviewPrayer?.points || selectedPreviewPrayer?.prayerPoints || []}
        timeSlot={formatTimeSlot(selectedPreviewPrayer?.time_slot)}
        dayWiseData={selectedPreviewPrayer?.days || {}}
        fromDate={selectedPreviewPrayer?.from_date}
        talkItOver={selectedPreviewPrayer?.talk_it_over !== 'no' && selectedPreviewPrayer?.talk_it_over !== false}
      />
      {/* Dropdown Menu for Row 3-Dots */}
      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl)}
        onClose={() => setRowMenu({ anchorEl: null, devotion: null })}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: 200,
            bgcolor: isLight ? "#FFFFFF" : "#1e293b",
            color: isLight ? "#0F172A" : "#FFFFFF",
            border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.15)",
            boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            py: 0.5,
          }
        }}
      >
        <MenuItem
          onClick={() => {
            const dev = rowMenu.devotion;
            setRowMenu({ anchorEl: null, devotion: null });
            if (dev) handleViewPrayerCloset(dev);
          }}
        >
          <ListItemIcon><VisibilityIcon sx={{ fontSize: 18, color: isLight ? "#0F766E" : "#64d2e2" }} /></ListItemIcon>
          <ListItemText primary="View Details" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            const dev = rowMenu.devotion;
            setRowMenu({ anchorEl: null, devotion: null });
            if (dev) handleOpenMobilePreview(dev);
          }}
        >
          <ListItemIcon><PhoneIphoneIcon sx={{ fontSize: 18, color: "#14B8A6" }} /></ListItemIcon>
          <ListItemText primary="User Preview" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            const dev = rowMenu.devotion;
            setRowMenu({ anchorEl: null, devotion: null });
            if (dev) {
              setSelectedClosetForActivity(dev);
              setUserActivityModalOpen(true);
            }
          }}
        >
          <ListItemIcon><TimelineIcon sx={{ fontSize: 18, color: "#06B6D4" }} /></ListItemIcon>
          <ListItemText primary="User Activity & Warriors" />
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {rowMenu.devotion?.status === 'active' ? (
          <MenuItem
            onClick={() => {
              const dev = rowMenu.devotion;
              setRowMenu({ anchorEl: null, devotion: null });
              if (dev) handleUnpublish(dev);
            }}
            sx={{ color: "#D97706" }}
          >
            <ListItemIcon><VisibilityOffIcon sx={{ fontSize: 18, color: "#D97706" }} /></ListItemIcon>
            <ListItemText primary="Unpublish Prayer" />
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              const dev = rowMenu.devotion;
              setRowMenu({ anchorEl: null, devotion: null });
              if (dev) handlePublish(dev);
            }}
            sx={{ color: "#10B981" }}
          >
            <ListItemIcon><PublishIcon sx={{ fontSize: 18, color: "#10B981" }} /></ListItemIcon>
            <ListItemText primary="Publish to App" />
          </MenuItem>
        )}

        {filterActiveTab === "archived" ? (
          <MenuItem
            onClick={() => {
              const dev = rowMenu.devotion;
              setRowMenu({ anchorEl: null, devotion: null });
              if (dev) handleRestore(dev.id);
            }}
            sx={{ color: "#10B981" }}
          >
            <ListItemIcon><UnarchiveIcon sx={{ fontSize: 18, color: "#10B981" }} /></ListItemIcon>
            <ListItemText primary="Restore Prayer" />
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              const dev = rowMenu.devotion;
              setRowMenu({ anchorEl: null, devotion: null });
              if (dev) handleArchive(dev);
            }}
            sx={{ color: "#F59E0B" }}
          >
            <ListItemIcon><ArchiveIcon sx={{ fontSize: 18, color: "#F59E0B" }} /></ListItemIcon>
            <ListItemText primary="Archive Prayer" />
          </MenuItem>
        )}

        {(user?.role === 1 || Number(user?.org_id) === Number(rowMenu.devotion?.org_id)) && (
          <MenuItem
            onClick={() => {
              const dev = rowMenu.devotion;
              setRowMenu({ anchorEl: null, devotion: null });
              if (dev) handleDelete(dev.id);
            }}
            sx={{ color: "#EF4444" }}
          >
            <ListItemIcon><DeleteIcon sx={{ fontSize: 18, color: "#EF4444" }} /></ListItemIcon>
            <ListItemText primary="Delete Prayer" />
          </MenuItem>
        )}

        {user?.role !== 1 && Number(user?.org_id) !== Number(rowMenu.devotion?.org_id) && (
          <MenuItem
            onClick={() => {
              const dev = rowMenu.devotion;
              setRowMenu({ anchorEl: null, devotion: null });
              if (dev) handleEnableDisable(dev.id, !dev.is_enabled);
            }}
          >
            <ListItemText primary={rowMenu.devotion?.is_enabled ? "Disable for Org" : "Enable for Org"} />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default SpecialPrayers;
